/**
 * PRO-3 — Product list.
 *
 * Columns: thumbnail, name, category, status badge (draft gray / active green / archived muted)
 * plus the derived "awaiting MOST pricing" chip when the product is ACTIVE but not yet
 * `is_priced` — the deliberate catalog gate (docs/04 §1.2). Row actions: edit, activate, archive.
 *
 * ANONYMITY: the producer sees THEIR OWN base prices only. There is no selling-price column,
 * because `ProducerProductListItem` carries no such field and `contract.ts` does not declare one.
 * `is_priced` says THAT the product is in the catalog, never FOR HOW MUCH.
 *
 * Note the list DTO (`ProducerProductListItem`) has no `category` and no price — the contract
 * gives us `{id, name, status, is_priced, thumb_url}` only. Rather than fire N detail requests
 * to fill a table, we render what the contract gives us and let the row link out to the wizard.
 * (Flagged in the handover: PRO-3 as specified in docs/06 wants a category column and the base
 * price, which the list endpoint does not return.)
 */
import * as React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, Boxes, ImageOff, Lock, Pencil, Plus, Send } from 'lucide-react';
import {
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  TableSkeleton,
  cn,
  useToast,
} from '@/components/ui';
import { PricingChip, ProductStatusBadge } from '@/components/StatusBadge';
import { qk } from '@/shared/queryClient';
import * as api from '@/shared/api';
import { canManageProducts, useMe } from '@/shared/session';
import type { ProducerProductListItem, ProductStatus } from '@/shared/contract';

const PER_PAGE = 25;

export function ProductsScreen() {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const [params, setParams] = useSearchParams();

  const status = (params.get('status') as ProductStatus | null) ?? undefined;
  const page = Number(params.get('page') ?? '1');

  const query = useQuery({
    queryKey: qk.products({ status, page, per_page: PER_PAGE }),
    queryFn: () => api.listProducts({ status, page, per_page: PER_PAGE }),
    enabled: canManageProducts(me),
  });

  // A producer awaiting approval may PREPARE drafts (docs/04 §1.1) — but docs/06 §3 specifies
  // PRO-3/PRO-4 render LOCKED with an explanatory empty state until approved. The spec wins.
  if (me && !canManageProducts(me)) {
    return (
      <div className="mx-auto max-w-7xl">
        <h1 className="mb-6 text-2xl font-semibold tracking-tight text-foreground">{t('products.title')}</h1>
        <EmptyState
          icon={Lock}
          title={t('products.lockedTitle')}
          body={t('products.lockedBody')}
          action={
            <Link
              to="/profile"
              className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              {t('products.lockedCta')}
            </Link>
          }
        />
      </div>
    );
  }

  const setStatus = (next: ProductStatus | undefined) => {
    const p = new URLSearchParams(params);
    if (next) p.set('status', next);
    else p.delete('status');
    p.delete('page');
    setParams(p);
  };

  const total = query.data?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PER_PAGE));

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{t('products.title')}</h1>
        <Link
          to="/products/new"
          className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Plus className="h-4 w-4" aria-hidden />
          {t('products.add')}
        </Link>
      </div>

      {/* Status filter */}
      <div className="flex flex-wrap gap-2">
        {([undefined, 'draft', 'active', 'archived'] as const).map((s) => (
          <button
            key={s ?? 'all'}
            type="button"
            onClick={() => setStatus(s)}
            className={cn(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              status === s
                ? 'border-foreground bg-foreground text-white'
                : 'border-input bg-card text-muted-foreground hover:bg-muted/60',
            )}
          >
            {s ? t(`status.${s}`) : t('products.filterAll')}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <TableSkeleton rows={6} cols={4} />
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : (query.data?.items.length ?? 0) === 0 ? (
        <EmptyState
          icon={Boxes}
          title={t('products.emptyTitle')}
          body={t('products.emptyBody')}
          action={
            <Link
              to="/products/new"
              className="inline-flex h-9 items-center gap-2 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="h-4 w-4" aria-hidden />
              {t('products.emptyCta')}
            </Link>
          }
        />
      ) : (
        <>
          <ProductTable items={query.data!.items} />

          {pages > 1 ? (
            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {t('common.page')} {page} {t('common.of')} {pages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => {
                    const p = new URLSearchParams(params);
                    p.set('page', String(page - 1));
                    setParams(p);
                  }}
                >
                  {t('common.back')}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pages}
                  onClick={() => {
                    const p = new URLSearchParams(params);
                    p.set('page', String(page + 1));
                    setParams(p);
                  }}
                >
                  {t('common.next')}
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

// ── Table ───────────────────────────────────────────────────────────────────────────────────
function ProductTable({ items }: { items: ProducerProductListItem[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [confirm, setConfirm] = React.useState<
    { kind: 'activate' | 'archive'; product: ProducerProductListItem } | null
  >(null);

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['producer', 'products'] });
  };

  const activate = useMutation({
    mutationFn: (id: string) => api.activateProduct(id),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
      // Activation does NOT publish. It sends the product to Merdan for pricing — say exactly
      // that, so the producer is not left wondering why buyers cannot see it.
      toast(t('products.activated'), 'success');
    },
    onError: () => setConfirm(null),
  });

  const archive = useMutation({
    mutationFn: (id: string) => api.archiveProduct(id),
    onSuccess: () => {
      setConfirm(null);
      invalidate();
      toast(t('common.saved'), 'success');
    },
    onError: () => setConfirm(null),
  });

  const pending = activate.isPending || archive.isPending;

  return (
    <>
      {/* Tables gain horizontal scroll on narrow screens (docs/06 §9). */}
      <Card className="overflow-x-auto">
        <table className="w-full min-w-[46rem] text-sm">
          <caption className="sr-only">{t('products.title')}</caption>
          <thead>
            <tr className="border-b border-border bg-muted/60 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th scope="col" className="w-16 px-4 py-3 font-medium">
                {t('products.thumb')}
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                {t('products.name')}
              </th>
              <th scope="col" className="px-4 py-3 font-medium">
                {t('products.status')}
              </th>
              <th scope="col" className="px-4 py-3 text-right font-medium">
                {t('products.actions')}
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-hairline">
            {items.map((p) => (
              <tr key={p.id} className="hover:bg-muted/60">
                <td className="px-4 py-3">
                  {/* Fixed 4:3 box prevents layout shift; neutral placeholder when there is no
                      photo (only possible on a draft — activation requires ≥1). docs/06 §10. */}
                  <div className="flex h-12 w-16 items-center justify-center overflow-hidden rounded border border-border bg-muted/60">
                    {p.thumb_url ? (
                      <img
                        src={p.thumb_url}
                        alt=""
                        loading="lazy"
                        width={64}
                        height={48}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <ImageOff className="h-4 w-4 text-muted-foreground/50" aria-hidden />
                    )}
                  </div>
                </td>

                <td className="px-4 py-3">
                  <Link
                    to={`/products/${p.id}/edit`}
                    className="font-medium text-foreground hover:text-link hover:underline"
                  >
                    {p.name}
                  </Link>
                </td>

                <td className="px-4 py-3">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <ProductStatusBadge status={p.status} />
                    {/* THE boolean. Never a price. */}
                    <PricingChip status={p.status} isPriced={p.is_priced} />
                  </div>
                </td>

                <td className="px-4 py-3">
                  <div className="flex items-center justify-end gap-1">
                    <Link
                      to={`/products/${p.id}/edit`}
                      className="inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-xs font-medium text-muted-foreground hover:bg-muted"
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden />
                      {t('common.edit')}
                    </Link>

                    {p.status === 'draft' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirm({ kind: 'activate', product: p })}
                      >
                        <Send className="h-3.5 w-3.5" aria-hidden />
                        {t('products.activate')}
                      </Button>
                    ) : null}

                    {p.status !== 'archived' ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setConfirm({ kind: 'archive', product: p })}
                      >
                        <Archive className="h-3.5 w-3.5" aria-hidden />
                        {t('products.archive')}
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Confirms NAME THE TARGET (docs/06 §4.1). */}
      <ConfirmDialog
        open={confirm?.kind === 'activate'}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm && activate.mutate(confirm.product.id)}
        loading={pending}
        title={t('products.activateConfirm', { name: confirm?.product.name ?? '' })}
        description={t('products.activateBody')}
        confirmLabel={t('products.activate')}
      />

      <ConfirmDialog
        open={confirm?.kind === 'archive'}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm && archive.mutate(confirm.product.id)}
        loading={pending}
        title={t('products.archiveConfirm', { name: confirm?.product.name ?? '' })}
        description={t('products.archiveBody')}
        confirmLabel={t('products.archive')}
        destructive
      />
    </>
  );
}
